import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import { useState, useCallback } from 'react';
import { PositionerPortal, Icon } from '@remirror/react-components';
import { useCommands, useRemirrorContext } from '@remirror/react-core';
import { usePositioner, useEvent } from '@remirror/react-hooks';
import { ComponentsTheme, ExtensionTablesTheme, getThemeVar } from '@remirror/theme';
import { findParentNodeOfType, range, throttle, isElementDomNode, command, ExtensionPriority, NodeExtension, convertCommand, composeTransactionSteps, getChangedNodes, replaceNodeAtPosition } from '@remirror/core';
import { blockNodePositioner, Positioner, hasStateChanged, defaultAbsolutePosition, isPositionVisible } from '@remirror/extension-positioner';
import { CellSelection, isInTable, selectedRect, addColSpan, tableNodeTypes, TableMap, updateColumnsOnResize, cellAround, isCellSelection, deleteRow, deleteColumn, tableEditing } from '@remirror/pm/tables';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Slice, Fragment as Fragment$1 } from '@remirror/pm/model';
import { PluginKey, Plugin, TextSelection } from '@remirror/pm/state';
import { DecorationSet, Decoration } from '@remirror/pm/view';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css } from '@emotion/css';
import { ReplaceAroundStep } from '@remirror/pm/transform';
import { h } from 'jsx-dom';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { createTableOptions, TableRowExtension as TableRowExtension$1, TableHeaderCellExtension as TableHeaderCellExtension$1, TableCellExtension as TableCellExtension$1, TableExtension as TableExtension$1, createTable } from '@remirror/extension-tables';
import { addFill } from '@remirror/icons';

var cellNodeTypes = ['tableCell', 'tableHeaderCell']; // When the selection is a CellSelection and there is only one cell in the selection, return the cell.

function findMenuTableCell(selection) {
  // If the selection is a CellSelection, then we show the cell menu inside the head cell.
  if (selection instanceof CellSelection) {
    return findParentNodeOfType({
      selection: selection.$head,
      types: cellNodeTypes
    });
  }

  return findParentNodeOfType({
    selection: selection,
    types: cellNodeTypes
  });
}
/**
 * Creates a positioner for the current table cell node which will show the
 * cell menu.
 *
 * It spans the full width and height of the table cell node.
 */


var menuCellPositioner = blockNodePositioner.clone(() => ({
  getActive: props => {
    var result = findMenuTableCell(props.state.selection);
    return result ? [result] : Positioner.EMPTY;
  }
}));

var ControllerType;

(function (ControllerType) {
  ControllerType[ControllerType["ROW_CONTROLLER"] = 1] = "ROW_CONTROLLER";
  ControllerType[ControllerType["COLUMN_CONTROLLER"] = 2] = "COLUMN_CONTROLLER";
  ControllerType[ControllerType["CORNER_CONTROLLER"] = 3] = "CORNER_CONTROLLER";
})(ControllerType || (ControllerType = {}));

var borderWidth = 1; // We could change it to a paramter instead of a constant if we want to support more border width values.

var DefaultTableCellMenuButton = _ref => {
  var setPopupOpen = _ref.setPopupOpen;
  return /*#__PURE__*/jsx("button", {
    onClick: () => {
      setPopupOpen(true);
    },
    onMouseDown: event => {
      // Stop the parent component from listening the onMouseDown event
      event.preventDefault();
      event.stopPropagation();
    },
    style: {
      position: 'relative',
      right: '0px',
      top: '0px',
      height: '16px',
      width: '16px',
      border: '1px solid blue',
      fontSize: '10px',
      lineHeight: '10px',
      cursor: 'pointer'
    },
    className: ComponentsTheme.BUTTON,
    children: "v"
  });
};

var DefaultTableCellMenuItem = props => /*#__PURE__*/jsx("button", {
  onClick: props.onClick,
  className: ComponentsTheme.MENU_ITEM,
  children: props.label
});

var DefaultTableCellMenuPopup = _ref2 => {
  var setPopupOpen = _ref2.setPopupOpen,
      popupOpen = _ref2.popupOpen;
  var commands = useCommands(); // close the popup after clicking

  var handleClick = command => {
    return () => {
      command();
      setPopupOpen(false);
    };
  }; // Notice that we won't close the popup after changing the cell background
  // because we want users to quick try multiple colors.


  var setTableCellBackground = color => {
    return () => {
      commands.setTableCellBackground(color);
    };
  };

  return /*#__PURE__*/jsxs("div", {
    style: {
      position: 'absolute',
      backgroundColor: 'white',
      width: '200px',
      border: '1px solid lightgray',
      display: popupOpen ? 'flex' : 'none',
      flexDirection: 'column'
    },
    className: ComponentsTheme.MENU,
    children: [/*#__PURE__*/jsx(DefaultTableCellMenuItem, {
      label: "Color teal",
      onClick: setTableCellBackground('teal')
    }), /*#__PURE__*/jsx(DefaultTableCellMenuItem, {
      label: "Color pink",
      onClick: setTableCellBackground('rgba(255,100,100,0.3)')
    }), /*#__PURE__*/jsx(DefaultTableCellMenuItem, {
      label: "Remove color",
      onClick: setTableCellBackground(null)
    }), /*#__PURE__*/jsx(DefaultTableCellMenuItem, {
      label: "Add row above",
      onClick: handleClick(commands.addTableRowBefore)
    }), /*#__PURE__*/jsx(DefaultTableCellMenuItem, {
      label: "Add row below",
      onClick: handleClick(commands.addTableRowAfter)
    }), /*#__PURE__*/jsx(DefaultTableCellMenuItem, {
      label: "Add column before",
      onClick: handleClick(commands.addTableColumnBefore)
    }), /*#__PURE__*/jsx(DefaultTableCellMenuItem, {
      label: "Add column after",
      onClick: handleClick(commands.addTableColumnAfter)
    }), /*#__PURE__*/jsx(DefaultTableCellMenuItem, {
      label: "Remove column",
      onClick: handleClick(commands.deleteTableColumn)
    }), /*#__PURE__*/jsx(DefaultTableCellMenuItem, {
      label: "Remove row",
      onClick: handleClick(commands.deleteTableRow)
    }), /*#__PURE__*/jsx(DefaultTableCellMenuItem, {
      label: "Remove table",
      onClick: handleClick(commands.deleteTable)
    })]
  });
};

var DefaultTableCellMenuComponent = props => {
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(DefaultTableCellMenuButton, _objectSpread({}, props)), /*#__PURE__*/jsx(DefaultTableCellMenuPopup, _objectSpread({}, props))]
  });
};

var TableCellMenu = _ref3 => {
  var _ref3$Component = _ref3.Component,
      Component = _ref3$Component === void 0 ? DefaultTableCellMenuComponent : _ref3$Component;
  var position = usePositioner(menuCellPositioner, []);
  var ref = position.ref,
      width = position.width,
      height = position.height,
      x = position.x,
      y = position.y;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      popupOpen = _useState2[0],
      setPopupOpen = _useState2[1]; // Hide the popup when users click.


  useEvent('mousedown', () => {
    if (popupOpen) {
      setPopupOpen(false);
    }

    return false;
  });
  return /*#__PURE__*/jsx(PositionerPortal, {
    children: /*#__PURE__*/jsx("div", {
      ref: ref,
      style: {
        position: 'absolute',
        left: x,
        top: y,
        width: width + borderWidth,
        height: height + borderWidth,
        zIndex: 100,
        pointerEvents: 'none',
        // place the child into the top-left corner
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-start' // for debug:
        // backgroundColor: 'lightpink',
        // opacity: 0.5,

      },
      children: /*#__PURE__*/jsx("div", {
        style: {
          zIndex: 100,
          pointerEvents: 'initial'
        },
        children: /*#__PURE__*/jsx(Component, {
          popupOpen: popupOpen,
          setPopupOpen: setPopupOpen
        })
      })
    })
  });
};

/* eslint-disable @typescript-eslint/no-non-null-assertion */
/**
 * Add a column at the given position in a table.
 *
 * Taken from https://github.com/ProseMirror/prosemirror-tables/blob/v1.1.1/src/commands.js#L39
 * Use the next column instead of the previous column as the reference, so that the controller
 * cell will not be duplicated.
 */

function addColumn(tr, _ref, col) {
  var map = _ref.map,
      tableStart = _ref.tableStart,
      table = _ref.table;
  var refColumn = col < map.width - 1 ? 0 : -1;

  for (var row = 0; row < map.height; row++) {
    var index = row * map.width + col; // If this position falls inside a col-spanning cell

    if (col > 0 && col < map.width && map.map[index - 1] === map.map[index]) {
      var pos = map.map[index];
      var cell = table.nodeAt(pos);
      tr.setNodeMarkup(tr.mapping.map(tableStart + pos), undefined, addColSpan(cell.attrs, col - map.colCount(pos))); // Skip ahead if rowspan > 1

      row += cell.attrs.rowspan - 1;
    } else {
      var type = table.nodeAt(map.map[index + refColumn]).type;

      var _pos = map.positionAt(row, col, table);

      tr.insert(tr.mapping.map(tableStart + _pos), type.createAndFill());
    }
  }

  return tr;
}
/**
 * Command to add a column before the column with the selection.
 */

function addColumnBefore(state, dispatch) {
  if (!isInTable(state)) {
    return false;
  }

  if (dispatch) {
    var rect = selectedRect(state);
    dispatch(addColumn(state.tr, rect, rect.left));
  }

  return true;
}
/**
 * Command to add a column after the column with the selection.
 */

function addColumnAfter(state, dispatch) {
  if (!isInTable(state)) {
    return false;
  }

  if (dispatch) {
    var rect = selectedRect(state);
    dispatch(addColumn(state.tr, rect, rect.right));
  }

  return true;
}
/**
 * Add a row at the given position in a table.
 *
 * Taken from https://github.com/ProseMirror/prosemirror-tables/blob/v1.1.1/src/commands.js#L127
 * Use the next row instead of the previous row as the reference, so that the controller
 * cell will not be duplicated.
 */

function addRow(tr, _ref2, row) {
  var map = _ref2.map,
      tableStart = _ref2.tableStart,
      table = _ref2.table;
  var rowPos = tableStart;

  for (var i = 0; i < row; i++) {
    rowPos += table.child(i).nodeSize;
  }

  var refRow = row < map.height - 1 ? 0 : -1;
  var cells = [];

  for (var col = 0, index = map.width * row; col < map.width; col++, index++) {
    // Covered by a rowspan cell
    if (row > 0 && row < map.height && map.map[index] === map.map[index - map.width]) {
      var pos = map.map[index];
      var attrs = table.nodeAt(pos).attrs;
      tr.setNodeMarkup(tableStart + pos, undefined, setAttr(attrs, 'rowspan', attrs.rowspan + 1));
      col += attrs.colspan - 1;
    } else {
      var type = table.nodeAt(map.map[index + refRow * map.width]).type;
      cells.push(type.createAndFill());
    }
  }

  tr.insert(rowPos, tableNodeTypes(table.type.schema).row.create(null, cells));
  return tr;
}
/**
 * Add a table row before the selection.
 */

function addRowBefore(state, dispatch) {
  if (!isInTable(state)) {
    return false;
  }

  if (dispatch) {
    var rect = selectedRect(state);
    dispatch(addRow(state.tr, rect, rect.top));
  }

  return true;
}
/**
 * Add a table row after the selection.
 */

function addRowAfter(state, dispatch) {
  if (!isInTable(state)) {
    return false;
  }

  if (dispatch) {
    var rect = selectedRect(state);
    dispatch(addRow(state.tr, rect, rect.bottom));
  }

  return true;
}

/**
 * Merge two DOMRect objects into a one big DOMRect object that contains both two DOMRect objects.
 */

function mergeDOMRects(rect1, rect2) {
  var left = Math.min(rect1.left, rect2.left);
  var right = Math.min(rect1.right, rect2.right);
  var top = Math.min(rect1.top, rect2.top);
  var bottom = Math.max(rect1.bottom, rect2.bottom);
  var width = right - left;
  var height = bottom - top;
  return new DOMRect(left, top, width, height);
}
function getRelativeCoord(absoluteCoord, parent) {
  var parentRect = parent.getBoundingClientRect();
  return {
    x: absoluteCoord.x + parent.scrollLeft - parentRect.left,
    y: absoluteCoord.y + parent.scrollTop - parentRect.top
  };
}

function shouldHideInsertButton(attrs, e) {
  if (attrs.col !== -1) {
    return e.clientX < attrs.triggerRect.left - 400 || e.clientX > attrs.triggerRect.right + 400 || e.clientY < attrs.triggerRect.top - 60 || e.clientY > attrs.triggerRect.bottom;
  } else if (attrs.row !== -1) {
    return e.clientX < attrs.triggerRect.left - 40 || e.clientX > attrs.triggerRect.right || e.clientY < attrs.triggerRect.top - 100 || e.clientY > attrs.triggerRect.bottom + 100;
  }

  return true;
}
var addFillIconCache = null; // TODO: this part is so ugly.

function AddFillIcon() {
  if (addFillIconCache) {
    return addFillIconCache;
  }

  var xmlns = 'http://www.w3.org/2000/svg';
  var svg = document.createElementNS(xmlns, 'svg');
  svg.setAttribute('xmlns', xmlns);
  svg.setAttribute('viewBox', '0 0 24 24');
  var g = document.createElementNS(xmlns, 'g');

  var _iterator = _createForOfIteratorHelper(addFill),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var tree = _step.value;
      var path = document.createElementNS(xmlns, tree.tag);

      for (var _i = 0, _Object$entries = Object.entries(tree.attr); _i < _Object$entries.length; _i++) {
        var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
            key = _Object$entries$_i[0],
            value = _Object$entries$_i[1];

        path.setAttribute(key, value);
      }

      g.append(path);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  svg.append(g);
  addFillIconCache = svg;
  return svg;
}

function InnerTableInsertButton(attrs) {
  var size = 18;
  return h('div', {
    className: "".concat(ExtensionTablesTheme.TABLE_INSERT_BUTTON, " ").concat(ExtensionTablesTheme.CONTROLLERS_TOGGLE),
    style: {
      top: "".concat(attrs.y - size / 2 - 5, "px"),
      left: "".concat(attrs.x - size / 2, "px")
    }
  }, AddFillIcon());
}

function TableInsertButton(_ref) {
  var view = _ref.view,
      tableRect = _ref.tableRect,
      attrs = _ref.attrs,
      removeInsertButton = _ref.removeInsertButton;
  var button = InnerTableInsertButton(attrs);

  var insertRolOrColumn = () => {
    var tr = view.state.tr;

    if (attrs.col !== -1) {
      tr = addColumn(tr, tableRect, attrs.col);
    } else if (attrs.row !== -1) {
      tr = addRow(tr, tableRect, attrs.row);
    } else {
      return;
    }

    view.dispatch(removeInsertButton(tr));
  };

  button.addEventListener('mousedown', () => {
    insertRolOrColumn();
  });
  return button;
}

// Change the attributes of the node at `pos`.
//
// This different between this function and `Transform.setNodeMarkup` is that it only overwrites fields that appear in `attrs`.
function setNodeAttrs(tr, pos, attrs, node) {
  node = node || tr.doc.nodeAt(pos);

  if (!node) {
    throw new RangeError('No node at given position');
  }

  var type = node.type;
  var newNode = type.create(_objectSpread(_objectSpread({}, node.attrs), attrs), undefined, node.marks);

  if (node.isLeaf) {
    return tr.replaceWith(pos, pos + node.nodeSize, newNode);
  }

  if (!type.validContent(node.content)) {
    throw new RangeError("Invalid content for node type ".concat(type.name));
  }

  return tr.step(new ReplaceAroundStep(pos, pos + node.nodeSize, pos + 1, pos + node.nodeSize - 1, new Slice(Fragment$1.from(newNode), 0, 0), 1, true));
} // TODO: https://github.com/ProseMirror/prosemirror-tables/pull/126
function cellSelectionToSelection(selection) {
  return selection;
}

class TableView {
  get dom() {
    return this.root;
  }

  get contentDOM() {
    return this.tbody;
  }

  constructor(node, cellMinWidth, decorations, view, getPos) {
    this.node = node;
    this.cellMinWidth = cellMinWidth;
    this.decorations = decorations;
    this.view = view;
    this.getPos = getPos;
    // console.debug('[TableView] constructor');
    this.map = TableMap.get(this.node);
    this.tbody = h('tbody', {
      className: ExtensionTablesTheme.TABLE_TBODY_WITH_CONTROLLERS
    });
    this.colgroup = h('colgroup', {
      className: ExtensionTablesTheme.TABLE_COLGROUP
    }, ...range(this.map.width).map(() => h('col')));
    this.table = h('table', {
      className: ExtensionTablesTheme.TABLE
    }, this.colgroup, this.tbody);
    this.insertButtonWrapper = h('div');
    this.root = h('div', null, this.table, this.insertButtonWrapper);
    this.render();
    this.showInsertButton = false;
    this.handleMouseMove = throttle(100, e => {
      if (this.showInsertButton) {
        var attrs = this.attrs().insertButtonAttrs;

        if (attrs && shouldHideInsertButton(attrs, e)) {
          this.showInsertButton = false;
          replaceChildren(this.insertButtonWrapper, []);

          if (this.removeInsertButton) {
            this.view.dispatch(this.removeInsertButton(this.view.state.tr));
          }
        }
      }
    });
    document.addEventListener('mousemove', this.handleMouseMove);
  }

  update(node, decorations) {
    // console.debug(`[TableView] update`);
    if (node.type !== this.node.type) {
      return false;
    }

    this.decorations = decorations;
    this.node = node;
    this.map = TableMap.get(this.node);
    this.render();
    return true;
  }

  render() {
    this.renderTable();

    if (!this.attrs().isControllersInjected) {
      return;
    }

    this.renderInsertButton();
  }

  renderTable() {
    if (this.colgroup.children.length !== this.map.width) {
      var cols = range(this.map.width).map(() => h('col'));
      replaceChildren(this.colgroup, cols);
    }

    var className = [ExtensionTablesTheme.TABLE, // Hide the table before controllers injected
    this.attrs().isControllersInjected ? ExtensionTablesTheme.TABLE_WITH_CONTROLLERS : ExtensionTablesTheme.TABLE_WAITTING_CONTROLLERS].join(' ');

    if (this.table.className !== className) {
      this.table.className = className;
    }

    updateColumnsOnResize(this.node, this.colgroup, this.table, this.cellMinWidth);
  }

  renderInsertButton() {
    var attrs = this.attrs().insertButtonAttrs;

    if (attrs) {
      var tableRect = {
        map: this.map,
        table: this.node,
        tableStart: this.getPos() + 1,
        // The following properties are not actually used
        left: 0,
        top: 0,
        right: 0,
        bottom: 0
      };

      this.removeInsertButton = tr => {
        // Remove insertButtonAttrs from tableNode so that the TableInsertButton won't keep at the origin position.
        var attrsPatch = {
          insertButtonAttrs: null
        };
        return setNodeAttrs(tr, tableRect.tableStart - 1, attrsPatch);
      };

      var button = TableInsertButton({
        view: this.view,
        attrs,
        tableRect,
        removeInsertButton: this.removeInsertButton
      });
      replaceChildren(this.insertButtonWrapper, [button]);
      this.showInsertButton = true;
    } else {
      replaceChildren(this.insertButtonWrapper, []);
      this.showInsertButton = false;
    }
  }

  attrs() {
    return this.node.attrs;
  }

  ignoreMutation() {
    return true;
  }

  destroy() {
    // console.debug('[TableView] destroy');
    document.removeEventListener('mousemove', this.handleMouseMove);
  }

} // TODO: this function's performance should be very bad. Maybe we should use some kind of DOM-diff algorithm.

function replaceChildren(parent, children) {
  while (parent.firstChild) {
    parent.firstChild.remove();
  }

  var _iterator = _createForOfIteratorHelper(children),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var child = _step.value;
      parent.append(child);
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
}

/* eslint-disable unicorn/no-abusive-eslint-disable */
// TODO: https://github.com/ProseMirror/prosemirror-tables/pull/131
function pointsAtCell($pos) {
  return $pos.parent.type.spec.tableRole == 'row' && $pos.nodeAfter;
} // TODO: https://github.com/ProseMirror/prosemirror-tables/pull/131

function setAttr(attrs, name, value) {
  var result = {};

  for (var prop in attrs) {
    result[prop] = attrs[prop];
  }

  result[name] = value;
  return result;
}
var key$1 = new PluginKey('tableColumnResizing');
function columnResizing() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$handleWidth = _ref.handleWidth,
      handleWidth = _ref$handleWidth === void 0 ? 5 : _ref$handleWidth,
      _ref$cellMinWidth = _ref.cellMinWidth,
      cellMinWidth = _ref$cellMinWidth === void 0 ? 25 : _ref$cellMinWidth;
      _ref.View;
      var _ref$lastColumnResiza = _ref.lastColumnResizable,
      lastColumnResizable = _ref$lastColumnResiza === void 0 ? true : _ref$lastColumnResiza,
      _ref$firstResizableCo = _ref.firstResizableColumn,
      firstResizableColumn = _ref$firstResizableCo === void 0 ? 0 : _ref$firstResizableCo;

  var plugin = new Plugin({
    key: key$1,
    state: {
      init(_, state) {
        // this.spec.props.nodeViews[tableNodeTypes(state.schema).table.name] =
        //   (node, view) => new View(node, cellMinWidth, view)
        return new ResizeState(-1, false);
      },

      apply(tr, prev) {
        return prev.apply(tr);
      }

    },
    props: {
      attributes(state) {
        var pluginState = key$1.getState(state);
        return pluginState.activeHandle > -1 ? {
          class: 'resize-cursor'
        } : null;
      },

      handleDOMEvents: {
        mousemove(view, event) {
          handleMouseMove(view, event, handleWidth, cellMinWidth, lastColumnResizable, firstResizableColumn);
        },

        mouseleave(view) {
          handleMouseLeave(view);
        },

        mousedown(view, event) {
          handleMouseDown(view, event, cellMinWidth);
        }

      },

      decorations(state) {
        var pluginState = key$1.getState(state);
        if (pluginState.activeHandle > -1) return handleDecorations(state, pluginState.activeHandle);
      },

      nodeViews: {}
    }
  });
  return plugin;
}

class ResizeState {
  constructor(activeHandle, dragging) {
    this.activeHandle = activeHandle;
    this.dragging = dragging;
  }

  apply(tr) {
    var state = this,
        action = tr.getMeta(key$1);
    if (action && action.setHandle != null) return new ResizeState(action.setHandle, null);
    if (action && action.setDragging !== undefined) return new ResizeState(state.activeHandle, action.setDragging);

    if (state.activeHandle > -1 && tr.docChanged) {
      var handle = tr.mapping.map(state.activeHandle, -1);
      if (!pointsAtCell(tr.doc.resolve(handle))) handle = null;
      state = new ResizeState(handle, state.dragging);
    }

    return state;
  }

}

function handleMouseMove(view, event, handleWidth, cellMinWidth, lastColumnResizable, firstResizableColumn) {
  var pluginState = key$1.getState(view.state);

  if (!pluginState.dragging) {
    var target = domCellAround(event.target),
        cell = -1;

    if (target) {
      var _target$getBoundingCl = target.getBoundingClientRect(),
          left = _target$getBoundingCl.left,
          right = _target$getBoundingCl.right;

      if (event.clientX - left <= handleWidth) cell = edgeCell(view, event, 'left');else if (right - event.clientX <= handleWidth) cell = edgeCell(view, event, 'right');
    }

    if (cell != pluginState.activeHandle) {
      if (cell !== -1) {
        var $cell = view.state.doc.resolve(cell);
        var table = $cell.node(-1),
            map = TableMap.get(table),
            start = $cell.start(-1);
        var col = map.colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan - 1;

        if (col < firstResizableColumn) {
          return;
        }

        if (!lastColumnResizable && col == map.width - 1) {
          return;
        }
      }

      updateHandle(view, cell);
    }
  }
}

function handleMouseLeave(view) {
  var pluginState = key$1.getState(view.state);
  if (pluginState.activeHandle > -1 && !pluginState.dragging) updateHandle(view, -1);
}

function handleMouseDown(view, event, cellMinWidth) {
  var pluginState = key$1.getState(view.state);
  if (pluginState.activeHandle == -1 || pluginState.dragging) return false;
  var cell = view.state.doc.nodeAt(pluginState.activeHandle);
  var width = currentColWidth(view, pluginState.activeHandle, cell.attrs);
  view.dispatch(view.state.tr.setMeta(key$1, {
    setDragging: {
      startX: event.clientX,
      startWidth: width
    }
  }));

  function finish(event) {
    window.removeEventListener('mouseup', finish);
    window.removeEventListener('mousemove', move);
    var pluginState = key$1.getState(view.state);

    if (pluginState.dragging) {
      updateColumnWidth(view, pluginState.activeHandle, draggedWidth(pluginState.dragging, event, cellMinWidth));
      view.dispatch(view.state.tr.setMeta(key$1, {
        setDragging: null
      }));
    }
  }

  function move(event) {
    if (!event.which) return finish(event);
    var pluginState = key$1.getState(view.state);
    var dragged = draggedWidth(pluginState.dragging, event, cellMinWidth);
    displayColumnWidth(view, pluginState.activeHandle, dragged, cellMinWidth);
  }

  window.addEventListener('mouseup', finish);
  window.addEventListener('mousemove', move);
  event.preventDefault();
  return true;
}

function currentColWidth(view, cellPos, _ref2) {
  var colspan = _ref2.colspan,
      colwidth = _ref2.colwidth;
  var width = colwidth && colwidth[colwidth.length - 1];
  if (width) return width;
  var dom = view.domAtPos(cellPos);
  var node = dom.node.childNodes[dom.offset];
  var domWidth = node.offsetWidth,
      parts = colspan;
  if (colwidth) for (var i = 0; i < colspan; i++) {
    if (colwidth[i]) {
      domWidth -= colwidth[i];
      parts--;
    }
  }
  return domWidth / parts;
}

function domCellAround(target) {
  while (target && target.nodeName != 'TD' && target.nodeName != 'TH') {
    target = target.classList.contains('ProseMirror') ? null : target.parentNode;
  }

  return target;
}

function edgeCell(view, event, side) {
  var found = view.posAtCoords({
    left: event.clientX,
    top: event.clientY
  });
  if (!found) return -1;
  var pos = found.pos;
  var $cell = cellAround(view.state.doc.resolve(pos));
  if (!$cell) return -1;
  if (side == 'right') return $cell.pos;
  var map = TableMap.get($cell.node(-1)),
      start = $cell.start(-1);
  var index = map.map.indexOf($cell.pos - start);
  return index % map.width == 0 ? -1 : start + map.map[index - 1];
}

function draggedWidth(dragging, event, cellMinWidth) {
  var offset = event.clientX - dragging.startX;
  return Math.max(cellMinWidth, dragging.startWidth + offset);
}

function updateHandle(view, value) {
  view.dispatch(view.state.tr.setMeta(key$1, {
    setHandle: value
  }));
}

function updateColumnWidth(view, cell, width) {
  var $cell = view.state.doc.resolve(cell);
  var table = $cell.node(-1),
      map = TableMap.get(table),
      start = $cell.start(-1);
  var col = map.colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan - 1;
  var tr = view.state.tr;

  for (var row = 0; row < map.height; row++) {
    var mapIndex = row * map.width + col; // Rowspanning cell that has already been handled

    if (row && map.map[mapIndex] == map.map[mapIndex - map.width]) continue;

    var pos = map.map[mapIndex],
        _table$nodeAt = table.nodeAt(pos),
        attrs = _table$nodeAt.attrs;

    var index = attrs.colspan == 1 ? 0 : col - map.colCount(pos);
    if (attrs.colwidth && attrs.colwidth[index] == width) continue;
    var colwidth = attrs.colwidth ? attrs.colwidth.slice() : zeroes(attrs.colspan);
    colwidth[index] = width;
    tr.setNodeMarkup(start + pos, null, setAttr(attrs, 'colwidth', colwidth));
  }

  if (tr.docChanged) view.dispatch(tr);
}

function displayColumnWidth(view, cell, width, cellMinWidth) {
  var $cell = view.state.doc.resolve(cell);
  var table = $cell.node(-1),
      start = $cell.start(-1);
  var col = TableMap.get(table).colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan - 1;
  var dom = view.domAtPos($cell.start(-1)).node;

  while (dom.nodeName != 'TABLE') {
    dom = dom.parentNode;
  }

  updateColumnsOnResize(table, dom.firstChild, dom, cellMinWidth, col, width);
}

function zeroes(n) {
  var result = [];

  for (var i = 0; i < n; i++) {
    result.push(0);
  }

  return result;
}

function handleDecorations(state, cell) {
  var decorations = [];
  var $cell = state.doc.resolve(cell);
  var table = $cell.node(-1),
      map = TableMap.get(table),
      start = $cell.start(-1);
  var col = map.colCount($cell.pos - start) + $cell.nodeAfter.attrs.colspan;

  for (var row = 0; row < map.height; row++) {
    var index = col + row * map.width - 1; // For positions that are have either a different cell or the end
    // of the table to their right, and either the top of the table or
    // a different cell above them, add a decoration

    if ((col == map.width || map.map[index] != map.map[index + 1]) && (row == 0 || map.map[index - 1] != map.map[index - 1 - map.width])) {
      var cellPos = map.map[index];
      var pos = start + cellPos + table.nodeAt(cellPos).nodeSize - 1;
      var dom = document.createElement('div');
      dom.className = 'column-resize-handle';
      decorations.push(Decoration.widget(pos, dom));
    }
  }

  return DecorationSet.create(state.doc, decorations);
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;
var preselectBorderColor = getThemeVar('color', 'table', 'preselect', 'border');
var preselectControllerBackgroundColor = getThemeVar('color', 'table', 'preselect', 'controller');
function getTableStyle(attrs) {
  var preselectClass = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    /* Make the border-style 'double' instead of 'solid'. This works because 'double' has a higher priority than 'solid' */\n    border-style: double;\n    border-color: ", ";\n  "])), preselectBorderColor);
  var preselectControllerClass = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    ", "\n    background-color: ", ";\n  "])), preselectClass, preselectControllerBackgroundColor);
  var classNames = '';

  if (attrs.preselectColumn !== -1) {
    classNames = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      & table.", " tbody tr {\n        th,\n        td {\n          &:nth-child(", ") {\n            ", ";\n          }\n        }\n        th.", ":nth-child(", ") {\n          ", "\n        }\n      }\n    "])), ExtensionTablesTheme.TABLE, attrs.preselectColumn + 1, preselectClass, ExtensionTablesTheme.TABLE_CONTROLLER, attrs.preselectColumn + 1, preselectControllerClass);
  } else if (attrs.preselectRow !== -1) {
    classNames = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      & table.", " tbody tr:nth-child(", ") {\n        td,\n        th {\n          ", ";\n        }\n        th.", " {\n          ", "\n        }\n      }\n    "])), ExtensionTablesTheme.TABLE, attrs.preselectRow + 1, preselectClass, ExtensionTablesTheme.TABLE_CONTROLLER, preselectControllerClass);
  } else if (attrs.preselectTable) {
    classNames = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      & table.", " tbody tr {\n        td,\n        th {\n          ", ";\n        }\n        th.", ".", " {\n          ", "\n        }\n      }\n    "])), ExtensionTablesTheme.TABLE, preselectClass, ExtensionTablesTheme.TABLE_CONTROLLER, ExtensionTablesTheme.TABLE_CONTROLLER, preselectControllerClass);
  }

  return classNames;
}
var key = new PluginKey('remirrorTableControllerPluginKey');
function createTableControllerPlugin() {
  return new Plugin({
    key: key,
    state: {
      init() {
        return new ControllerState({});
      },

      apply(tr, prev) {
        return prev.apply(tr);
      }

    },
    props: {
      decorations: state => {
        var controllerState = key.getState(state);

        if (!controllerState) {
          return null;
        }

        var _controllerState$valu = controllerState.values,
            tableNodeResult = _controllerState$valu.tableNodeResult,
            predelete = _controllerState$valu.predelete;

        if (tableNodeResult) {
          var styleClassName = getTableStyle(controllerState.values);
          var className = "".concat(ExtensionTablesTheme.TABLE_SHOW_CONTROLLERS, " ").concat(styleClassName);

          if (predelete) {
            className += " ".concat(ExtensionTablesTheme.TABLE_SHOW_PREDELETE);
          }

          var decorations = [Decoration.node(tableNodeResult.pos, tableNodeResult.end, {
            class: className
          })];
          return DecorationSet.create(state.doc, decorations);
        }

        return null;
      }
    }
  });
}

class ControllerState {
  constructor(action) {
    this.action = action;
    this.values = _objectSpread({
      tableNodeResult: null,
      preselectTable: false,
      preselectColumn: -1,
      preselectRow: -1,
      predelete: false,
      insertButtonAttrs: null
    }, action);
  }

  apply(tr) {
    this.values.tableNodeResult = findParentNodeOfType({
      types: 'table',
      selection: tr.selection
    });
    var props = tr.getMeta(key);

    if (props) {
      return new ControllerState(_objectSpread(_objectSpread({}, this.values), props));
    }

    return this;
  }

}

function setControllerPluginMeta(tr, props) {
  return tr.setMeta(key, props);
}

function repeat(val, times) {
  var result = [];

  for (var i = 0; i < times; i++) {
    result.push(val);
  }

  return result;
}

function injectControllers(_ref) {
  var schema = _ref.schema,
      getMap = _ref.getMap,
      oldTable = _ref.table;
  var controllerCell = schema.nodes.tableControllerCell.create();
  var headerControllerCells = repeat(controllerCell, getMap().width + 1);
  var controllerRow = schema.nodes.tableRow.create({}, headerControllerCells);
  var newRowsArray = [controllerRow];
  var oldRows = oldTable.content;
  oldRows.forEach(oldRow => {
    var oldCells = oldRow.content;
    var newCells = Fragment$1.from(controllerCell).append(oldCells);
    var newRow = oldRow.copy(newCells);
    newRowsArray.push(newRow);
  });
  var newRows = Fragment$1.fromArray(newRowsArray);
  var newTable = oldTable.copy(newRows); // Warning:
  // prosemirror-model will build up a single reusable default attribute
  // object, and use it for all nodes that don't specify specific
  // attributes for node types where all attrs have a default value. That
  // means we shouldn't directly update an attribute from a node's attrs.

  newTable.attrs = _objectSpread(_objectSpread({}, newTable.attrs), {}, {
    isControllersInjected: true
  });
  return newTable;
}
function createControllerEvents(_ref2) {
  var view = _ref2.view,
      findTable = _ref2.findTable;
  return {
    onClick: event => {
      var axis = getCellAxisByMouseEvent(view, event);

      if (axis) {
        if (axis.row > 0) {
          selectRow(view, findTable, axis.row);
        } else if (axis.col > 0) {
          selectColumn(view, findTable, axis.col);
        } else {
          selectTable(view, findTable);
        }
      }
    },
    onMouseEnter: event => {
      var axis = getCellAxisByMouseEvent(view, event);

      if (axis) {
        if (axis.row > 0) {
          setPreselectRow(view, axis.row);
        } else if (axis.col > 0) {
          setPreselectColumn(view, axis.col);
        } else {
          setPreselectTable(view, true);
        }
      }
    },
    onMouseLeave: () => {
      resetControllerPluginMeta(view);
    }
  };
}

function onlyTableFound(func) {
  return function (view, findTable) {
    var found = findTable();

    if (!found) {
      return;
    }

    for (var _len = arguments.length, extra = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
      extra[_key - 2] = arguments[_key];
    }

    return func(view, found, ...extra);
  };
}

var selectRow = onlyTableFound((view, table, index) => {
  var map = TableMap.get(table.node);
  var cellIndex = getCellIndex(map, index, 0);
  var tr = view.state.tr;
  var posInTable = map.map[cellIndex + 1];
  var pos = table.pos + posInTable + 1;
  var $pos = tr.doc.resolve(pos);
  var selection = CellSelection.rowSelection($pos);
  tr = tr.setSelection(cellSelectionToSelection(selection));
  view.dispatch(tr);
});
var selectColumn = onlyTableFound((view, table, index) => {
  var map = TableMap.get(table.node);
  var cellIndex = getCellIndex(map, 0, index);
  var tr = view.state.tr;
  var posInTable = map.map[cellIndex];
  var pos = table.pos + posInTable + 1;
  var $pos = tr.doc.resolve(pos);
  var selection = CellSelection.colSelection($pos);
  tr = tr.setSelection(cellSelectionToSelection(selection));
  view.dispatch(tr);
});
var selectTable = onlyTableFound((view, table) => {
  var map = TableMap.get(table.node);

  if (map.map.length > 0) {
    var tr = view.state.tr;
    var firstCellPosInTable = map.map[0];
    var lastCellPosInTable = map.map[map.map.length - 1];
    var firstCellPos = table.pos + firstCellPosInTable + 1;
    var lastCellPos = table.pos + lastCellPosInTable + 1;
    var $firstCellPos = tr.doc.resolve(firstCellPos);
    var $lastCellPos = tr.doc.resolve(lastCellPos);
    var selection = new CellSelection($firstCellPos, $lastCellPos);
    tr = tr.setSelection(cellSelectionToSelection(selection));
    view.dispatch(tr);
  }
});

function setPreselectRow(view, index) {
  view.dispatch(setControllerPluginMeta(view.state.tr, {
    preselectRow: index
  }));
}

function setPreselectColumn(view, index) {
  view.dispatch(setControllerPluginMeta(view.state.tr, {
    preselectColumn: index
  }));
}

function setPreselectTable(view, value) {
  view.dispatch(setControllerPluginMeta(view.state.tr, {
    preselectTable: value
  }));
}

function setPredelete(view, value) {
  view.dispatch(setControllerPluginMeta(view.state.tr, {
    predelete: value
  }));
}

function resetControllerPluginMeta(view) {
  view.dispatch(setControllerPluginMeta(view.state.tr, {
    preselectRow: -1,
    preselectColumn: -1,
    preselectTable: false,
    predelete: false
  }));
}

function getCellIndex(map, rowIndex, colIndex) {
  return map.width * rowIndex + colIndex;
}
function getCellAxisByMouseEvent(view, event) {
  var domCell = domCellAround(event.target);

  if (!domCell) {
    return null;
  }

  var domCellRect = domCell.getBoundingClientRect(); // we can't directly use the mouse event's clientX and clientY because when the mouse
  // is insert the trigger area, which it may or may not be insert the ccontroller cell.
  // we use "+ 1" to make sure that the corrds is inside the cell dom element.

  return getCellAxisByCoords(view, {
    left: domCellRect.left + 1,
    top: domCellRect.top + 1
  });
}
function getCellAxisByCoords(view, coords) {
  var cellPos = view.posAtCoords(coords);

  if (!cellPos) {
    return null;
  }

  var $cell = cellAround(view.state.doc.resolve(cellPos.pos));

  if (!$cell) {
    return null;
  }

  var map = TableMap.get($cell.node(-1));
  var start = $cell.start(-1);
  var rect = map.findCell($cell.pos - start);
  var col = rect.left,
      row = rect.top;
  return {
    col,
    row
  };
}
var CellSelectionType;

(function (CellSelectionType) {
  CellSelectionType[CellSelectionType["row"] = 1] = "row";
  CellSelectionType[CellSelectionType["col"] = 2] = "col";
  CellSelectionType[CellSelectionType["table"] = 3] = "table";
  CellSelectionType[CellSelectionType["other"] = 4] = "other";
})(CellSelectionType || (CellSelectionType = {}));

function getCellSelectionType(selection) {
  if (selection.isRowSelection()) {
    if (selection.isColSelection()) {
      return CellSelectionType.table;
    }

    return CellSelectionType.row;
  } else if (selection.isColSelection()) {
    return CellSelectionType.col;
  }

  return CellSelectionType.other;
}

function createDeleteButtonPositioner() {
  return Positioner.create({
    hasChanged: hasStateChanged,

    getActive(props) {
      var state = props.state;
      var selection = state.selection;

      if (isCellSelection(selection)) {
        var cellSelectionType = getCellSelectionType(selection);

        if (cellSelectionType === CellSelectionType.col || cellSelectionType === CellSelectionType.row) {
          var tableResult = findParentNodeOfType({
            types: 'table',
            selection
          });

          if (tableResult) {
            var positionerData = {
              tableResult,
              cellSelectionType,
              anchorCellPos: selection.$anchorCell.pos,
              headCellPos: selection.$headCell.pos
            };
            return [positionerData];
          }
        }
      }

      return Positioner.EMPTY;
    },

    getPosition(props) {
      var view = props.view,
          data = props.data;
      var anchorCellDOM = view.nodeDOM(data.anchorCellPos);
      var headCellDOM = view.nodeDOM(data.headCellPos);

      if (!anchorCellDOM || !headCellDOM || !isElementDomNode(anchorCellDOM) || !isElementDomNode(headCellDOM)) {
        return defaultAbsolutePosition;
      }

      var map = TableMap.get(data.tableResult.node); // Don't show the delete button if there is only one row/column (excluded controllers).

      if (data.cellSelectionType === CellSelectionType.col && map.width <= 2) {
        return defaultAbsolutePosition;
      } else if (data.cellSelectionType === CellSelectionType.row && map.height <= 2) {
        return defaultAbsolutePosition;
      }

      var anchorCellRect = anchorCellDOM.getBoundingClientRect();
      var headCellRect = headCellDOM.getBoundingClientRect();
      var rect = mergeDOMRects(anchorCellRect, headCellRect);
      var editorRect = view.dom.getBoundingClientRect(); // The width and height of the current selected block node.

      var height = rect.height;
      var width = rect.width; // The top and left relative to the parent `editorRect`.

      var left = view.dom.scrollLeft + rect.left - editorRect.left;
      var top = view.dom.scrollTop + rect.top - editorRect.top;
      var visible = isPositionVisible(rect, view.dom);
      var margin = 16;
      return data.cellSelectionType === CellSelectionType.row ? {
        rect,
        visible,
        height: 0,
        width: 0,
        x: left - margin,
        y: top + height / 2
      } : {
        rect,
        visible,
        height: 0,
        width: 0,
        x: left + width / 2,
        y: top - margin
      };
    }

  });
}

var TableDeleteRowColumnInnerButton = _ref => {
  var position = _ref.position,
      onClick = _ref.onClick,
      onMouseOver = _ref.onMouseOver,
      onMouseOut = _ref.onMouseOut;
  var size = 18;
  return /*#__PURE__*/jsx("div", {
    ref: position.ref,
    onMouseDown: e => {
      // onClick doesn't work. I don't know why.
      onClick(e);
    },
    onMouseOver: onMouseOver,
    onMouseOut: onMouseOut,
    style: {
      '--remirror-table-delete-button-y': "".concat(position.y, "px"),
      '--remirror-table-delete-button-x': "".concat(position.x, "px")
    },
    className: ExtensionTablesTheme.TABLE_DELETE_ROW_COLUMN_INNER_BUTTON,
    children: /*#__PURE__*/jsx(Icon, {
      name: "closeFill",
      size: size,
      color: '#ffffff'
    })
  });
};
var deleteButtonPositioner = createDeleteButtonPositioner();

function usePosition() {
  var position = usePositioner(deleteButtonPositioner, []);
  return position;
}

function useEvents(view) {
  var handleClick = useCallback(() => {
    var selection = view.state.selection;

    if (isCellSelection(selection)) {
      var cellSelectionType = getCellSelectionType(selection);

      if (cellSelectionType === CellSelectionType.row) {
        deleteRow(view.state, view.dispatch);
      } else if (cellSelectionType === CellSelectionType.col) {
        deleteColumn(view.state, view.dispatch);
      }
    }
  }, [view]);
  var handleMouseOver = useCallback(() => setPredelete(view, true), [view]);
  var handleMouseOut = useCallback(() => setPredelete(view, false), [view]);
  return {
    handleClick,
    handleMouseOver,
    handleMouseOut
  };
}

var TableDeleteRowColumnButton = _ref2 => {
  var _Component;

  var Component = _ref2.Component;

  var _useRemirrorContext = useRemirrorContext(),
      view = _useRemirrorContext.view;

  var position = usePosition();

  var _useEvents = useEvents(view),
      handleClick = _useEvents.handleClick,
      handleMouseOver = _useEvents.handleMouseOver,
      handleMouseOut = _useEvents.handleMouseOut;

  Component = (_Component = Component) !== null && _Component !== void 0 ? _Component : TableDeleteRowColumnInnerButton;
  return /*#__PURE__*/jsx(PositionerPortal, {
    children: /*#__PURE__*/jsx(Component, {
      position: position,
      onClick: handleClick,
      onMouseOver: handleMouseOver,
      onMouseOut: handleMouseOut
    })
  });
};

var defaultTableComponentsProps = {
  enableTableCellMenu: true,
  enableTableDeleteRowColumnButton: true
};
var TableComponents = props => {
  props = _objectSpread(_objectSpread({}, defaultTableComponentsProps), props);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [props.enableTableCellMenu ? /*#__PURE__*/jsx(TableCellMenu, _objectSpread({}, props.tableCellMenuProps)) : null, props.enableTableDeleteRowColumnButton ? /*#__PURE__*/jsx(TableDeleteRowColumnButton, _objectSpread({}, props.tableDeleteRowColumnButtonProps)) : null]
  });
};

var TriggerAreaType;

(function (TriggerAreaType) {
  TriggerAreaType[TriggerAreaType["ADD_COLUMN_BEFORE"] = 1] = "ADD_COLUMN_BEFORE";
  TriggerAreaType[TriggerAreaType["ADD_COLUMN_AFTER"] = 2] = "ADD_COLUMN_AFTER";
  TriggerAreaType[TriggerAreaType["ADD_ROW_BEFORE"] = 3] = "ADD_ROW_BEFORE";
  TriggerAreaType[TriggerAreaType["ADD_ROW_AFTER"] = 4] = "ADD_ROW_AFTER";
})(TriggerAreaType || (TriggerAreaType = {}));

function buildInsertButtonAttrs(type, triggerRect, editorDom, cellAxis) {
  var row = cellAxis.row,
      col = cellAxis.col;
  var relativeCoord = getRelativeCoord(triggerRect, editorDom);

  switch (type) {
    case TriggerAreaType.ADD_COLUMN_BEFORE:
      return {
        triggerRect,
        x: relativeCoord.x - borderWidth,
        y: relativeCoord.y + 12,
        row: -1,
        col: col
      };

    case TriggerAreaType.ADD_COLUMN_AFTER:
      return {
        triggerRect,
        x: relativeCoord.x + triggerRect.width,
        y: relativeCoord.y + 12,
        row: -1,
        col: col + 1
      };

    case TriggerAreaType.ADD_ROW_BEFORE:
      return {
        triggerRect,
        x: relativeCoord.x + 12,
        y: relativeCoord.y + 5 - borderWidth,
        row: row,
        col: -1
      };

    default:
      return {
        triggerRect,
        x: relativeCoord.x + 12,
        y: relativeCoord.y + 5 + triggerRect.height,
        row: row + 1,
        col: -1
      };
  }
}

function showButton(trigger, findTable, type, axis, view) {
  var triggerRect = trigger === null || trigger === void 0 ? void 0 : trigger.getBoundingClientRect();

  if (!triggerRect || !(triggerRect.width || triggerRect.height)) {
    return;
  }

  var tableResult = findTable();

  if (!tableResult) {
    return;
  }

  var insertButtonAttrs = buildInsertButtonAttrs(type, triggerRect, view.dom, axis);
  view.dispatch(setNodeAttrs(view.state.tr, tableResult.pos, {
    insertButtonAttrs
  }));
}

var TriggerArea = _ref => {
  var isTopLeft = _ref.isTopLeft,
      view = _ref.view,
      findTable = _ref.findTable;
  var trigger = h('div', {
    className: ExtensionTablesTheme.TABLE_CONTROLLER_TRIGGER_AREA,
    onMouseEnter: event => {
      var axis = getCellAxisByMouseEvent(view, event);

      if (axis) {
        var type;

        if (axis.row > 0) {
          type = isTopLeft ? TriggerAreaType.ADD_ROW_BEFORE : TriggerAreaType.ADD_ROW_AFTER;
        } else if (axis.col > 0) {
          type = isTopLeft ? TriggerAreaType.ADD_COLUMN_BEFORE : TriggerAreaType.ADD_COLUMN_AFTER;
        }

        if (type) {
          showButton(trigger, findTable, type, axis, view);
        }
      }
    }
  });
  return trigger;
};

var TableInsertButtonTrigger = _ref2 => {
  var view = _ref2.view,
      findTable = _ref2.findTable;
  return [TriggerArea({
    view,
    findTable,
    isTopLeft: true
  }), TriggerArea({
    view,
    findTable,
    isTopLeft: false
  })];
};

var TableInsertButtonTrigger$1 = TableInsertButtonTrigger;

var TableInsertMark = () => {
  return [h('div', {
    className: ExtensionTablesTheme.TABLE_CONTROLLER_MARK_ROW_CORNER
  }), h('div', {
    className: ExtensionTablesTheme.TABLE_CONTROLLER_MARK_COLUMN_CORNER
  })];
};

var TableInsertMark$1 = TableInsertMark;

var TableControllerCell = _ref => {
  var view = _ref.view,
      getPos = _ref.getPos,
      contentDOM = _ref.contentDOM;

  var findTable = () => {
    return findParentNodeOfType({
      types: 'table',
      selection: view.state.doc.resolve(getPos())
    });
  };

  var events = createControllerEvents({
    view,
    findTable
  });
  var wrapper = h('div', {
    contentEditable: 'false',
    className: ExtensionTablesTheme.TABLE_CONTROLLER_WRAPPER
  }, contentDOM, ...TableInsertButtonTrigger$1({
    view,
    findTable
  }), ...TableInsertMark$1());
  return h('th', _objectSpread({
    contentEditable: 'false',
    className: "".concat(ExtensionTablesTheme.TABLE_CONTROLLER, " ").concat(ExtensionTablesTheme.CONTROLLERS_TOGGLE),
    dataset: {
      controllerCell: ''
    }
  }, events), wrapper);
};

var TableControllerCell$1 = TableControllerCell;

class TableControllerCellView {
  constructor(node, view, getPos) {
    this.node = node;
    this.view = view;
    this.getPos = getPos;
    this.contentDOM = h('div', {
      contentEditable: false
    });
    this.dom = TableControllerCell$1({
      view,
      getPos,
      contentDOM: this.contentDOM
    });
  } // When a DOM mutation happens (eg: the button show or hide), don't let
  // ProseMirror re-render the view.


  ignoreMutation() {
    return true;
  } // Don't let ProseMirror to handle the dom event (eg: onclick).


  stopEvent() {
    return true;
  }

}

var _dec, _dec2, _dec3, _dec4, _dec5, _class;
var TableExtension = (_dec = command(createTableOptions), _dec2 = command(), _dec3 = command(), _dec4 = command(), _dec5 = command(), (_class = class TableExtension extends TableExtension$1 {
  get name() {
    return 'table';
  }

  createNodeViews() {
    return (node, view, getPos, decorations) => {
      return new TableView(node, 10, decorations, view, getPos);
    };
  }
  /**
   * Add the table plugins to the editor.
   */


  createExternalPlugins() {
    var plugins = [];

    if (this.options.resizable) {
      // Add first to avoid highlighting cells while resizing
      plugins.push(columnResizing({
        firstResizableColumn: 1
      }));
    }

    plugins.push(tableEditing(), createTableControllerPlugin());
    return plugins;
  }

  createNodeSpec(extra) {
    var spec = {
      isolating: true,
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        isControllersInjected: {
          default: false
        },
        insertButtonAttrs: {
          default: null
        }
      }),
      content: 'tableRow+',
      tableRole: 'table',
      parseDOM: [{
        tag: 'table',
        getAttrs: extra.parse
      }],

      toDOM(node) {
        return ['table', ['tbody', extra.dom(node), 0]];
      },

      allowGapCursor: false
    };
    return spec;
  }
  /**
   * Create the table extensions. Set the priority to low so that they appear
   * lower down in the node list.
   */


  createExtensions() {
    return [new TableRowExtension({
      priority: ExtensionPriority.Low
    })];
  }

  onView() {
    // We have multiple node types which share a eom table_row in this
    // extentison. In order to make the function `tableNodeTypes` from
    // `prosemirror-extension-tables` return the correct node type, we
    // need to overwrite `schema.cached.tableNodeTypes`.
    var schema = this.store.schema;
    schema.cached.tableNodeTypes = {
      cell: schema.nodes.tableCell,
      row: schema.nodes.tableRow,
      table: schema.nodes.table,
      header_cell: schema.nodes.tableHeaderCell
    };
  }
  /**
   * Create a table in the editor at the current selection point.
   */


  createTable() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return props => {
      var tr = props.tr,
          dispatch = props.dispatch,
          state = props.state;

      if (!tr.selection.empty) {
        return false;
      }

      var schema = state.schema;
      var offset = tr.selection.anchor + 1;
      var table = createTable(_objectSpread({
        schema
      }, options));
      var controlledTable = injectControllers({
        schema,
        getMap: () => TableMap.get(table),
        table
      });
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.replaceSelectionWith(controlledTable).scrollIntoView().setSelection(TextSelection.near(tr.doc.resolve(offset))));
      return true;
    };
  }
  /**
   * Command to add a column before the column with the selection.
   */


  addTableColumnBefore() {
    return convertCommand(addColumnBefore);
  }
  /**
   * Command to add a column after the column with the selection.
   */


  addTableColumnAfter() {
    return convertCommand(addColumnAfter);
  }
  /**
   * Add a table row before the current selection.
   */


  addTableRowBefore() {
    return convertCommand(addRowBefore);
  }
  /**
   * Add a table row after the current selection.
   */


  addTableRowAfter() {
    return convertCommand(addRowAfter);
  }

  createPlugin() {
    return {
      appendTransaction: (transactions, prevState, state) => {
        var composedTransaction = composeTransactionSteps(transactions, prevState);
        var schema = state.schema,
            tr = state.tr;
        var tableNodes = getChangedNodes(composedTransaction, {
          predicate: _ref => {
            var type = _ref.type;
            return type === schema.nodes.table;
          }
        });

        if (tableNodes.length === 0) {
          return;
        }

        var _iterator = _createForOfIteratorHelper(tableNodes),
            _step;

        try {
          var _loop = function _loop() {
            var _step$value = _step.value,
                table = _step$value.node,
                pos = _step$value.pos;

            if (table.attrs.isControllersInjected) {
              return "continue";
            }

            var controlledTable = injectControllers({
              schema,
              getMap: () => TableMap.get(table),
              table
            });
            replaceNodeAtPosition({
              pos,
              tr,
              content: controlledTable
            });
          };

          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var _ret = _loop();

            if (_ret === "continue") continue;
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }

        return tr.steps.length > 0 ? tr : undefined;
      }
    };
  }

}, (_applyDecoratedDescriptor(_class.prototype, "createTable", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "createTable"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addTableColumnBefore", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "addTableColumnBefore"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addTableColumnAfter", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "addTableColumnAfter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addTableRowBefore", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "addTableRowBefore"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addTableRowAfter", [_dec5], Object.getOwnPropertyDescriptor(_class.prototype, "addTableRowAfter"), _class.prototype)), _class));
class TableRowExtension extends TableRowExtension$1 {
  get name() {
    return 'tableRow';
  }

  createNodeSpec(extra, override) {
    var spec = super.createNodeSpec(extra, override);
    spec.content = '(tableCell | tableHeaderCell | tableControllerCell)*';

    spec.toDOM = node => {
      return ['tr', extra.dom(node), 0];
    };

    spec.allowGapCursor = false;
    return spec;
  }
  /**
   * Automatically create the `TableCellExtension`,`TableHeaderCellExtension`
   * and `TableControllerCellExtension`. This is placed here so that this
   * extension can be tested independently from the `TableExtension`.
   */


  createExtensions() {
    return [new TableCellExtension({
      priority: ExtensionPriority.Low
    }), new TableHeaderCellExtension({
      priority: ExtensionPriority.Low
    })];
  }

}
class TableHeaderCellExtension extends TableHeaderCellExtension$1 {
  get name() {
    return 'tableHeaderCell';
  }

  createNodeSpec(extra, override) {
    var spec = super.createNodeSpec(extra, override);
    spec.attrs = _objectSpread({}, spec.attrs);
    spec.allowGapCursor = false;
    return spec;
  }

  createExtensions() {
    return [new TableControllerCellExtension({
      priority: ExtensionPriority.Low
    })];
  }

}
class TableCellExtension extends TableCellExtension$1 {
  get name() {
    return 'tableCell';
  }

  createNodeSpec(extra, override) {
    var spec = super.createNodeSpec(extra, override);
    spec.allowGapCursor = false;
    return spec;
  }

}
class TableControllerCellExtension extends NodeExtension {
  get name() {
    return 'tableControllerCell';
  }

  createNodeSpec(extra) {
    var cellAttrs = _objectSpread(_objectSpread({}, extra.defaults()), {}, {
      colspan: {
        default: 1
      },
      rowspan: {
        default: 1
      },
      colwidth: {
        default: null
      },
      background: {
        default: null
      }
    });

    return {
      atom: true,
      isolating: true,
      content: 'block*',
      attrs: cellAttrs,
      tableRole: 'header_cell',

      toDOM() {
        return ['th', {
          'data-controller-cell': ''
        }, 0];
      },

      allowGapCursor: false
    };
  }

  createNodeViews() {
    return (node, view, getPos) => {
      return new TableControllerCellView(node, view, getPos);
    };
  }

  createExtensions() {
    return [];
  }

}

export { TableCellExtension, TableCellMenu, TableComponents, TableControllerCellExtension, TableDeleteRowColumnButton, TableDeleteRowColumnInnerButton, TableExtension, TableHeaderCellExtension, TableRowExtension, key as tableControllerPluginKey };
